/**
 * Speech out for voice mode: markdown → speakable text, a sentence stream that turns a growing
 * streamed reply into complete sentences (so speech starts before the reply finishes), and a
 * cancellable speaker over the Web Speech synthesis API (barge-in stops it at once).
 */

/** Markdown and URLs read aloud badly: keep the words, drop the syntax. */
export function speakable(md: string): string {
  return md
    .replace(/```[\s\S]*?```/g, '. Code omitted. ')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/https?:\/\/([^/\s]+)[^\s]*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/(\*\*|__|\*|_|~~)(\S[^*_~]*?)\1/g, '$2')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s*(?:[-*+]|\d+[.)])\s+/gm, '')
    .replace(/^\s*>\s?/gm, '')
    .replace(/\|/g, ' ')
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l, i, all) => (i < all.length - 1 && !/[.!?:;]$/.test(l) ? `${l}.` : l))
    .join(' ')
    .replace(/^[.\s]+/, '')
    .replace(/\s*\.\s*\./g, '.')
    .replace(/\s+/g, ' ')
    .trim()
}

const ABBREV = /\b(e\.g|i\.e|etc|vs|mr|mrs|dr|st|approx|no)\.$/i

/** Complete sentences from a reply that keeps growing (streamed), each emitted exactly once. */
export class SentenceStream {
  private text = ''
  private at = 0

  /** Feed the whole reply so far; returns newly completed sentences. */
  push(full: string): string[] {
    if (!full.startsWith(this.text.slice(0, this.at))) this.at = 0
    this.text = full
    const out: string[] = []
    const re = /[.!?](?=\s)|\n{2,}/g
    re.lastIndex = this.at
    for (let m = re.exec(full); m; m = re.exec(full)) {
      const end = m.index + m[0].length
      const piece = full.slice(this.at, end)
      if (/\d\.$/.test(piece) && /^\d/.test(full.slice(end).trimStart())) continue
      if (ABBREV.test(piece.trim())) continue
      const s = piece.trim()
      if (s) out.push(s)
      this.at = end
    }
    return out
  }

  /** The reply finished: everything not yet emitted. */
  end(full: string): string[] {
    const out = this.push(full)
    const rest = this.text.slice(this.at).trim()
    this.at = this.text.length
    return rest ? [...out, rest] : out
  }
}

interface SynthLike {
  speak(u: SpeechSynthesisUtterance): void
  cancel(): void
}

/**
 * Queue of spoken chunks for one reply. `onIdle` fires once the reply is closed and everything
 * queued was spoken; a cancelled reply never fires it (the state machine already moved on).
 */
export class Speaker {
  private pending = 0
  private closed = false
  private generation = 0
  private live = true

  constructor(
    private readonly o: {
      synth: SynthLike
      utterance: (text: string) => SpeechSynthesisUtterance
      onIdle: () => void
      voice?: () => { lang?: string; rate?: number } | undefined
    },
  ) {}

  /** Start a new reply (after a cancel). */
  begin() {
    this.generation++
    this.pending = 0
    this.closed = false
    this.live = true
  }

  say(text: string) {
    if (!this.live || !text.trim()) return
    const gen = this.generation
    const u = this.o.utterance(text)
    const v = this.o.voice?.()
    if (v?.lang) u.lang = v.lang
    if (v?.rate) u.rate = v.rate
    this.pending++
    const settle = () => {
      if (gen !== this.generation) return
      this.pending = Math.max(0, this.pending - 1)
      this.check()
    }
    u.onend = settle
    u.onerror = settle
    this.o.synth.speak(u)
  }

  /** No more chunks for this reply. */
  close() {
    this.closed = true
    this.check()
  }

  cancel() {
    this.generation++
    this.live = false
    this.pending = 0
    this.closed = false
    this.o.synth.cancel()
  }

  private check() {
    if (this.live && this.closed && this.pending === 0) {
      this.live = false
      this.o.onIdle()
    }
  }
}

/** The browser's synthesizer, or undefined where speech output is unavailable. */
export function browserSynth(): SynthLike | undefined {
  const g = globalThis as { speechSynthesis?: SynthLike }
  return g.speechSynthesis
}
